/**
 * useCustomerMailTeamMutation: MV: write the client's mail-visibility team.
 *
 * live mode → PATCH /customers/:subjectType/:subjectId/mail-team, then invalidate
 *             the ['customers'] key family so BOTH surfaces refresh: the Kunden grid
 *             and the client page (which reads the same list endpoint and finds its
 *             row by subject_id).
 * mock mode → resolves null without a network call, mirroring useCustomerStatusMutation.
 *
 * The api owns the rules (rule #2): 403 without 'visibility.manage', 404 for a team
 * outside the tenant. This hook surfaces failures rather than judging them.
 *
 * [Source: apps/web/src/features/customers/hooks/useCustomerStatusMutation.ts]
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CustomerMailTeamDto } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';

export interface CustomerMailTeamChange {
  subjectType: 'person' | 'firma';
  subjectId: string;
  /** null lifts the restriction: everyone in the tenant may see this client's mail. */
  teamId: string | null;
}

export function useCustomerMailTeamMutation() {
  const qc = useQueryClient();
  const live = isLiveData();

  return useMutation<CustomerMailTeamDto | null, Error, CustomerMailTeamChange>({
    mutationFn: ({ subjectType, subjectId, teamId }) => {
      if (!live) return Promise.resolve(null);
      return apiClient.customers.setMailTeam(subjectType, subjectId, teamId);
    },
    onSuccess: () => {
      // Covers qk.customers({...filters}) AND qk.customers({detail}): both are
      // ['customers','list', …]. The pointer lives on the list row, so a stale
      // grid/detail would keep showing the previous team.
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}
